/**
 * Network error detection and handling utilities
 */

// Default timeout in milliseconds (15 seconds)
export const DEFAULT_TIMEOUT = 15000;

/**
 * Custom error class for server errors (5xx)
 */
export class ServerError extends Error {
  constructor(message = 'Something went wrong on our end. Please try again later.', statusCode = 500) {
    super(message);
    this.name = 'ServerError';
    this.isServerError = true;
    this.statusCode = statusCode;
  }
}

/**
 * Checks if an HTTP status code indicates a server error (5xx)
 * @param {number} statusCode - The HTTP status code
 * @returns {boolean} - True if it's a server error
 */
export const isServerErrorStatus = (statusCode) => {
  return statusCode >= 500 && statusCode < 600;
};

/**
 * Checks if an error is a server error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a server error
 */
export const isServerError = (error) => {
  if (error instanceof ServerError) {
    return true;
  }
  if (error?.name === 'ServerError' || error?.isServerError) {
    return true;
  }
  if (error?.statusCode && isServerErrorStatus(error.statusCode)) {
    return true;
  }
  return false;
};

/**
 * Returns a user-friendly message for server errors
 * @returns {string} - User-friendly error message
 */
export const getServerErrorMessage = () => {
  return 'Something went wrong on our end. Please try again later.';
};

/**
 * Custom error class for timeout errors
 */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out. Please try again.') {
    super(message);
    this.name = 'TimeoutError';
    this.isTimeoutError = true;
  }
}

/**
 * Checks if an error is a timeout error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a timeout error
 */
export const isTimeoutError = (error) => {
  if (error instanceof TimeoutError) {
    return true;
  }
  if (error?.name === 'TimeoutError' || error?.isTimeoutError) {
    return true;
  }
  // Check for AbortError which is thrown when fetch is aborted
  if (error?.name === 'AbortError') {
    return true;
  }
  const errorMessage = error?.message?.toLowerCase() || '';
  return errorMessage.includes('timeout') ||
         errorMessage.includes('timed out') ||
         errorMessage.includes('aborted');
};

/**
 * Returns a user-friendly message for timeout errors
 * @returns {string} - User-friendly error message
 */
export const getTimeoutErrorMessage = () => {
  return 'The request is taking too long. Please check your connection and try again.';
};

/**
 * Checks if an error is a network/connection error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a network error
 */
export const isNetworkError = (error) => {
  // Check if it's already a NetworkError instance
  if (error instanceof NetworkError) {
    return true;
  }

  // Check for isNetworkError flag (set by NetworkError class)
  if (error?.isNetworkError || error?.name === 'NetworkError') {
    return true;
  }

  // TypeError with "Failed to fetch" is the standard network error
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true;
  }

  // Check for common network error messages
  const networkErrorMessages = [
    'Failed to fetch',
    'Network request failed',
    'Network Error',
    'NetworkError',
    'net::ERR_INTERNET_DISCONNECTED',
    'net::ERR_NETWORK_CHANGED',
    'net::ERR_CONNECTION_REFUSED',
    'net::ERR_CONNECTION_RESET',
    'net::ERR_CONNECTION_TIMED_OUT',
    'net::ERR_NAME_NOT_RESOLVED',
    'The Internet connection appears to be offline',
    'A network error occurred',
    'Unable to connect to the server',
    'Cross-Origin Request Blocked',
    'CORS',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
  ];

  const errorMessage = error?.message?.toLowerCase() || '';
  return networkErrorMessages.some(msg =>
    errorMessage.includes(msg.toLowerCase())
  );
};

/**
 * Returns a user-friendly message for network errors
 * @returns {string} - User-friendly error message
 */
export const getNetworkErrorMessage = () => {
  return 'Unable to connect to the server. Please check your internet connection and try again.';
};

/**
 * Custom error class for network errors
 */
export class NetworkError extends Error {
  constructor(message = 'Network connection failed') {
    super(message);
    this.name = 'NetworkError';
    this.isNetworkError = true;
  }
}

/**
 * Fetches with a timeout - aborts the request if it takes too long
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 15000)
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchWithTimeout = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Check for network errors FIRST (before checking AbortError)
    // This ensures CORS errors and connection failures are properly categorized
    if (isNetworkError(error)) {
      throw new NetworkError(getNetworkErrorMessage());
    }

    // Convert AbortError to TimeoutError for better user messaging
    // This handles cases where our timeout triggered the abort
    if (error.name === 'AbortError') {
      throw new TimeoutError(getTimeoutErrorMessage());
    }

    throw error;
  }
};

/**
 * Wraps a fetch call and throws NetworkError for network failures
 * @param {Promise} fetchPromise - The fetch promise
 * @returns {Promise} - The fetch result or throws NetworkError
 */
export const fetchWithNetworkErrorHandling = async (fetchPromise) => {
  try {
    return await fetchPromise;
  } catch (error) {
    if (isTimeoutError(error)) {
      throw new TimeoutError(getTimeoutErrorMessage());
    }
    if (isNetworkError(error)) {
      throw new NetworkError(getNetworkErrorMessage());
    }
    throw error;
  }
};

/**
 * Determines the type of error and returns appropriate message
 * @param {Error} error - The error to analyze
 * @returns {Object} - Object with errorType and message
 */
export const categorizeError = (error) => {
  if (isTimeoutError(error)) {
    return {
      type: 'timeout',
      message: error.message || getTimeoutErrorMessage(),
      isRecoverable: true,
    };
  }
  if (isNetworkError(error)) {
    return {
      type: 'network',
      message: error.message || getNetworkErrorMessage(),
      isRecoverable: true,
    };
  }
  if (isServerError(error)) {
    return {
      type: 'server',
      message: getServerErrorMessage(),
      isRecoverable: true,
    };
  }
  return {
    type: 'unknown',
    message: error.message || 'An unexpected error occurred.',
    isRecoverable: false,
  };
};

export default {
  DEFAULT_TIMEOUT,
  TimeoutError,
  isTimeoutError,
  getTimeoutErrorMessage,
  isNetworkError,
  getNetworkErrorMessage,
  NetworkError,
  ServerError,
  isServerError,
  isServerErrorStatus,
  getServerErrorMessage,
  fetchWithTimeout,
  fetchWithNetworkErrorHandling,
  categorizeError,
};
